import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, FolderSearch } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";

import { MigrationType } from "../../../../models/Statistics/MigrationType";
import {
  FlaggedPathStatus,
  FlaggedPathSummary,
  UpdateFlaggedPathRequest,
} from "../../../../models/FlaggedPath";
import FlaggedPathsRepository from "../../../../repositories/FlaggedPathsRepository";
import { QUERY_KEYS } from "../../../../constants/QueryKeys";
import { FlaggedPathModal } from "../../../FlaggedPaths/FlaggedPathModal";
import TableEmptyState from "../../TableEmptyState";

interface FlaggedPathResolveViewProps {
  organizationId: string;
  /**
   * The entity's business key — case number, intake number, provider name id, folder path or template type.
   * Not the row id: occurrences are keyed by the business key, because a deleted and re-discovered entity comes
   * back under a new row id while its key survives.
   */
  migrationEntityId: string;
  /** Pairs with the key so a path reached by a different entity of another type cannot appear here. */
  migrationType: MigrationType;
  /** Singular, lowercase noun for the entity ("case", "intake", …) used in copy. */
  entityNoun: string;
  /** Query key(s) to invalidate once a decision is saved — the entity's own row carries a flagged-paths marker. */
  invalidateQueryKeys: readonly unknown[][];
  disabled?: boolean;
}

/**
 * Drawer listing the folders discovery declined on this entity's behalf, and the way into the decision about
 * each one.
 * </summary>
 * <remarks>
 * Sits beside <c>ConflictResolveView</c> and behaves like it on purpose: the entity's row carries an indicator
 * only, and anything actually done about it is done here, on the entity's own page, without leaving it.
 *
 * The decision itself is delegated to <c>FlaggedPathModal</c> rather than reimplemented. A flagged path is one
 * row however many entities reach it, so allowing it is a decision with reach beyond this entity — the modal is
 * where that is laid out, along with the reason prompt an unassigned editor has to answer.
 */
export const FlaggedPathResolveView: React.FC<FlaggedPathResolveViewProps> = ({
  organizationId,
  migrationEntityId,
  migrationType,
  entityNoun,
  invalidateQueryKeys,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<FlaggedPathSummary | null>(null);
  // Constructed plainly, not memoized: the constructor calls useApi(), so wrapping it would be a hook inside a
  // hook. Every repository in this codebase is built the same way for the same reason.
  const repository = new FlaggedPathsRepository();
  const queryClient = useQueryClient();

  const queryKey = [QUERY_KEYS.FLAGGED_PATHS, organizationId, migrationEntityId, migrationType] as const;

  // Live only: a cleared path is one nothing reports any more, and the point of this panel is what is still
  // outstanding for this entity. The org-wide page is where a settled decision is revisited.
  const pathsQuery = useQuery({
    queryKey,
    queryFn: async () =>
      (
        await repository.getFlaggedPaths(organizationId, {
          migrationEntityId,
          migrationType,
          status: FlaggedPathStatus.Live,
          pageSize: 200,
        })
      ).data,
    // Fetched on mount rather than when the drawer opens, so the count is on the button before it is clicked —
    // the same way the conflicts drawer behaves. Bounded: this component only renders for an entity whose row
    // already says it has flagged paths.
    enabled: !disabled,
    staleTime: 30 * 1000,
  });

  const paths = pathsQuery.data ?? [];

  const handleSave = async (flaggedPathId: string, request: UpdateFlaggedPathRequest) => {
    await repository.updateFlaggedPath(organizationId, flaggedPathId, request);
    await queryClient.invalidateQueries({ queryKey });
    await Promise.all(invalidateQueryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    setEditing(null);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Button
        variant="outline"
        size="sm"
        className={cn("min-w-44 gap-2", colorVariantClasses.warning.text)}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <AlertTriangle className="size-4" />
        Flagged paths
        {paths.length > 0 && (
          <Badge variant="warning" className="ml-1 px-1.5">
            {paths.length}
          </Badge>
        )}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="sm:max-w-2xl w-full gap-0">
          <SheetHeader className="border-b">
            <SheetTitle className="flex items-center gap-2">
              <AlertTriangle className={cn("size-5", colorVariantClasses.warning.text)} />
              Flagged paths
            </SheetTitle>
            <SheetDescription>
              Folders discovery declined to walk for this {entityNoun}, so their files were never picked up.
              Allowing one lets discovery take it on the next cycle.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-4">
            {pathsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : pathsQuery.isError ? (
              <p className="text-sm text-destructive">Could not load this {entityNoun}'s flagged paths.</p>
            ) : paths.length === 0 ? (
              <TableEmptyState
                icon={FolderSearch}
                title="Nothing outstanding"
                description={`Discovery is not declining any folder for this ${entityNoun}.`}
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {paths.map((path) => (
                  <li key={path.id}>
                    <button
                      type="button"
                      onClick={() => setEditing(path)}
                      className="w-full rounded-md border p-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="block break-all text-sm font-medium">{path.path}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-2">
                        {path.liveEntityCount > 1 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="neutral">
                                Reached by {path.liveEntityCount} entities
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              One decision about this folder applies wherever it is allowed, so it reaches beyond
                              this {entityNoun}.
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {path.recurredOccurrenceCount > 0 && (
                          <Badge variant="warning">Came back</Badge>
                        )}
                        {path.lastSeenOn && (
                          <span className="text-xs text-muted-foreground">
                            Last reported {new Date(path.lastSeenOn).toLocaleDateString()}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {editing && (
        <FlaggedPathModal
          open
          flaggedPath={editing}
          organizationId={organizationId}
          searchTerm={migrationEntityId}
          onClose={() => setEditing(null)}
          onSave={(request) => handleSave(editing.id, request)}
        />
      )}
    </TooltipProvider>
  );
};

export default FlaggedPathResolveView;
